import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Crown, Megaphone } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OverviewTab } from "@/components/admin/OverviewTab";
import { MyWorkspacesTab } from "@/components/admin/MyWorkspacesTab";
import { CompAccountsTab } from "@/components/admin/CompAccountsTab";
import { AdminsTab } from "@/components/admin/AdminsTab";
import { FeatureFlagsTab } from "@/components/admin/FeatureFlagsTab";
import { FinancesTab } from "@/components/admin/FinancesTab";
import { AffiliateTab } from "@/components/admin/AffiliateTab";
import { AiRoutingTab } from "@/components/admin/AiRoutingTab";
import { AiUsageTab } from "@/components/admin/AiUsageTab";
import { CreditAuditTab } from "@/components/admin/CreditAuditTab";
import { RagSettingsTab } from "@/components/admin/RagSettingsTab";
import { HandleControlsTab } from "@/components/admin/HandleControlsTab";
import { DesignSettingsTab } from "@/components/admin/DesignSettingsTab";
import { CslStylesSyncCard } from "@/components/admin/CslStylesSyncCard";
import { AppSeo } from "@/components/AppSeo";

const LEGACY_FINANCE_TABS = new Set(["billing", "pnl", "workspace-costs", "payouts"]);
const LEGACY_MESH_TAB = "mesh";

const SuperAdmin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "overview";

  // Back-compat: redirect old top-level finance tabs into Finances + sub.
  useEffect(() => {
    if (LEGACY_FINANCE_TABS.has(rawTab)) {
      setSearchParams((sp) => {
        sp.set("tab", "finances");
        sp.set("sub", rawTab);
        return sp;
      }, { replace: true });
    } else if (rawTab === LEGACY_MESH_TAB) {
      setSearchParams((sp) => {
        sp.set("tab", "design");
        sp.set("section", "mesh");
        return sp;
      }, { replace: true });
    }
  }, [rawTab, setSearchParams]);

  const activeTab = LEGACY_FINANCE_TABS.has(rawTab)
    ? "finances"
    : rawTab === LEGACY_MESH_TAB
      ? "design"
      : rawTab;
  useUserRole();

  return (
    <>
    <AppSeo title="Super admin" />
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-serif text-3xl font-semibold flex items-center gap-2">
          <Crown className="h-6 w-6 text-primary" /> Super Admin
        </h1>
        <Button onClick={() => navigate("/admin/announcements")} variant="outline">
          <Megaphone className="h-4 w-4" /> Announcements
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setSearchParams((sp) => { sp.set("tab", v); sp.delete("sub"); return sp; })}>
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="my-workspaces">My Personas</TabsTrigger>
            <TabsTrigger value="comp-accounts">Comp Accounts</TabsTrigger>
            <TabsTrigger value="admins">Admins</TabsTrigger>
            <TabsTrigger value="feature-flags">Feature flags</TabsTrigger>
            <TabsTrigger value="finances">Finances</TabsTrigger>
            <TabsTrigger value="affiliate">Affiliate</TabsTrigger>
            <TabsTrigger value="handles">Handles</TabsTrigger>
            <TabsTrigger value="ai-routing">AI routing</TabsTrigger>
            <TabsTrigger value="ai-usage">AI usage</TabsTrigger>
            <TabsTrigger value="credits">Credits</TabsTrigger>
            <TabsTrigger value="rag">RAG</TabsTrigger>
            <TabsTrigger value="design">Design settings</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4">
          <OverviewTab />
          <CslStylesSyncCard />
        </TabsContent>
        <TabsContent value="my-workspaces" className="space-y-4"><MyWorkspacesTab /></TabsContent>
        <TabsContent value="comp-accounts" className="space-y-4"><CompAccountsTab /></TabsContent>
        <TabsContent value="admins" className="space-y-4"><AdminsTab /></TabsContent>
        <TabsContent value="feature-flags" className="space-y-4"><FeatureFlagsTab /></TabsContent>
        <TabsContent value="finances" className="space-y-4"><FinancesTab /></TabsContent>
        <TabsContent value="affiliate" className="space-y-4"><AffiliateTab /></TabsContent>
        <TabsContent value="handles" className="space-y-4"><HandleControlsTab /></TabsContent>
        <TabsContent value="ai-routing" className="space-y-4"><AiRoutingTab /></TabsContent>
        <TabsContent value="ai-usage" className="space-y-4"><AiUsageTab /></TabsContent>
        <TabsContent value="credits" className="space-y-4"><CreditAuditTab /></TabsContent>
        <TabsContent value="rag" className="space-y-4"><RagSettingsTab /></TabsContent>
        <TabsContent value="design" className="space-y-4"><DesignSettingsTab /></TabsContent>
      </Tabs>
    </div>
    </>
  );
};

export default SuperAdmin;
